(function () {
  'use strict';
  const defaultApi = 'https://top-serw-appka.vercel.app/api/search-symbol';
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cleanQuery = value => String(value || '').normalize('NFKC').replace(/\s+/g, ' ').trim().slice(0, 160);
  const abortError = () => new DOMException('Request aborted', 'AbortError');

  async function request(url, options, format) {
    options = options || {};
    if (options.signal && options.signal.aborted) throw abortError();
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (options.signal) options.signal.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, options.timeout || 6000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: options.credentials || 'omit', headers: { Accept: format === 'text' ? 'text/html' : 'application/json' } });
      if (!response.ok) throw new Error('Search request failed (' + response.status + ')');
      const data = await response[format || 'json']();
      if (controller.signal.aborted) throw abortError();
      return data;
    } finally {
      clearTimeout(timer);
      if (options.signal) options.signal.removeEventListener('abort', abort);
    }
  }

  function searchUrl(query, params) {
    const url = new URL(root() + 'search', window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', cleanQuery(query));
    Object.entries(params || {}).forEach(([key, value]) => {
      if (!['q', 'denios_q', 'ts_order', 'ts_exact', 'section_id'].includes(key) && value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
    });
    return url.pathname + url.search;
  }

  async function search(query, options) {
    options = options || {};
    const config = window.TopserwSearchConfig || {};
    const url = new URL(config.apiUrl || defaultApi, window.location.origin);
    url.searchParams.set('q', cleanQuery(query));
    url.searchParams.set('lite', '1');
    url.searchParams.set('type', options.type || 'product');
    ['page', 'limit', 'sort_by', 'available', 'vendor', 'product_type', 'collection', 'min_price', 'max_price'].forEach(key => {
      if (options[key] !== undefined && options[key] !== null && options[key] !== '') url.searchParams.set(key, options[key]);
    });
    const data = await request(url.toString(), options);
    if (data.error || data._error) throw new Error(data.error || data._error);
    if (!Array.isArray(data.edges) || !Number.isFinite(data.total)) throw new Error('Search index response is incomplete');
    return data;
  }

  function sameStoreUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url.pathname + url.search : null;
    } catch (_) { return null; }
  }

  function categoryFacets(facets) {
    const source = document.querySelector('[data-ts-search-categories]');
    let menu;
    try { menu = JSON.parse(source && source.textContent || '[]'); } catch (_) { return []; }
    if (!Array.isArray(menu) || !Array.isArray(facets)) return [];
    const counts = new Map(facets.filter(item => item && typeof item.value === 'string' && Number.isInteger(item.count) && item.count >= 0).map(item => [item.value, item.count]));
    const seen = new Set();
    return menu.filter(item => {
      if (!item || typeof item.value !== 'string' || typeof item.label !== 'string' || !item.label.trim() || !counts.has(item.value) || seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    }).map(item => ({ value: item.value, label: item.label, count: counts.get(item.value), depth: Number.isInteger(item.depth) ? Math.min(2, Math.max(0, item.depth)) : 0 }));
  }

  async function nativeLookup(query, types, options) {
    const url = new URL(root() + 'search/suggest.json', window.location.origin);
    url.searchParams.set('q', cleanQuery(query));
    url.searchParams.set('resources[type]', types);
    url.searchParams.set('resources[limit]', String(options.limit || 6));
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    const data = await request(url.toString(), { ...options, credentials: 'same-origin' });
    return data.resources && data.resources.results || {};
  }

  async function nativeSuggestions(query, options) {
    const results = await nativeLookup(query, 'query,collection', options || {});
    return {
      queries: (results.queries || []).map(item => ({ text: item.text, url: searchUrl(item.text) })).filter(item => item.text),
      collections: (results.collections || []).map(item => ({ title: item.title, url: sameStoreUrl(item.url) })).filter(item => item.title && item.url)
    };
  }

  async function nativeProducts(query, options) {
    options = options || {};
    const results = await nativeLookup(query, 'product', options);
    const products = (results.products || []).filter(item => !options.product_type || item.type === options.product_type || item.product_type === options.product_type);
    return { edges: products.map(item => ({ node: { title: item.title, handle: item.handle } })), total: null };
  }

  function predictiveCard(template) {
    const data = template.dataset;
    const link = document.createElement('a');
    link.className = 'search-bar__result-item';
    link.href = sameStoreUrl(data.url) || root() + 'products/' + encodeURIComponent(data.handle);
    if (data.image) {
      const holder = document.createElement('div');
      holder.className = 'search-bar__image-container';
      const aspect = document.createElement('div');
      aspect.className = 'aspect-ratio aspect-ratio--square';
      const image = document.createElement('img');
      image.src = data.image;
      image.alt = '';
      image.width = 120;
      image.height = 120;
      image.loading = 'lazy';
      aspect.append(image);
      holder.append(aspect);
      link.append(holder);
    }
    const info = document.createElement('div');
    info.className = 'search-bar__item-info';
    const title = document.createElement('p');
    title.className = 'search-bar__item-title';
    title.textContent = data.title;
    info.append(title);
    [['sku', 'SKU'], ['symbol', 'Symbol']].forEach(([key, label]) => {
      if (!data[key] || (key === 'symbol' && data.symbol === data.sku)) return;
      const line = document.createElement('span');
      line.className = 'search-bar__item-' + key + ' text--small text--subdued';
      line.style.display = 'block';
      line.textContent = label + ': ' + data[key];
      info.append(line);
    });
    const price = template.content.querySelector('.product-item__price-list, .price-list');
    if (price) {
      const copy = price.cloneNode(true);
      copy.classList.add('search-bar__item-price');
      copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      info.append(copy);
    }
    link.append(info);
    return link;
  }

  async function renderProducts(handles, options) {
    options = options || {};
    if (options.signal && options.signal.aborted) throw abortError();
    const unique = Array.from(new Set(handles));
    if (unique.some(handle => typeof handle !== 'string' || !/^[a-z0-9][a-z0-9_-]*$/i.test(handle))) throw new Error('Invalid product handle');
    const templates = new Map();
    for (let offset = 0; offset < unique.length; offset += 24) {
      const batch = unique.slice(offset, offset + 24);
      const url = new URL(root() + 'search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', batch.map(handle => 'handle:"' + handle + '"').join(' OR '));
      url.searchParams.set('section_id', 'topserw-search-products');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('options[unavailable_products]', 'show');
      const html = await request(url.toString(), { ...options, credentials: 'same-origin' }, 'text');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      parsed.querySelectorAll('template[data-ts-search-product]').forEach(template => templates.set(template.dataset.handle, template));
    }
    const fragment = document.createDocumentFragment();
    unique.forEach(handle => {
      const template = templates.get(handle);
      if (!template || !template.content.querySelector('.product-item')) throw new Error('Nie udało się pobrać karty produktu: ' + handle);
      if (options.mode === 'predictive') fragment.append(predictiveCard(template));
      else fragment.append(template.content.cloneNode(true));
    });
    return fragment;
  }

  window.TopserwSearch = { search, renderProducts, searchUrl, nativeSuggestions, nativeProducts, categoryFacets };
})();
